<template>
    <teleport to="body">
        <transition name="season-modal" @after-enter="focusStart" @after-leave="restoreFocus">
            <div v-if="open" class="season-modal" :class="{'is-new-year': newYear}" data-cy="season_modal"
                 @mousedown.self="close" @keydown="onKeydown">
                <div class="season-modal-sky" aria-hidden="true">
                    <season-sky v-if="effects" :key="newYear ? 'confetti' : 'snow'" :density="0.9" :max-size="5"
                                :speed="0.7" :confetti="newYear" :glints="8"></season-sky>
                </div>

                <section ref="dialog" class="season-modal-dialog" role="dialog" aria-modal="true"
                         aria-labelledby="season-modal-title" tabindex="-1">
                    <!-- Céu do cartão: estrelinhas cintilando e um brilho que respira atrás do título -->
                    <span class="season-modal-aura" aria-hidden="true"></span>
                    <span class="season-modal-twinkles" aria-hidden="true">
                        <i v-for="star in twinkles" :key="star.id" :style="star.style"></i>
                    </span>

                    <!-- Enfeites finos pendurados no alto, balançando de leve -->
                    <span class="season-modal-hangers" aria-hidden="true">
                        <span v-for="hanger in hangers" :key="hanger.id" class="season-hanger"
                              :class="`is-${hanger.kind}`" :style="hanger.style">
                            <svg v-if="hanger.kind === 'star'" viewBox="-12 -12 24 24"><path :d="newYear ? sparklePath : starPath"/></svg>
                        </span>
                    </span>

                    <!-- Relendo o Advento depois do Natal: volta para o cartão de fim de ano -->
                    <button v-if="reviewing" type="button" class="season-modal-back" data-cy="season_modal_review_back"
                            @click="stopReview">
                        <i class="icon-chevron-left" aria-hidden="true"></i> Voltar ao cartão
                    </button>

                    <button type="button" class="season-modal-close" aria-label="Fechar" data-cy="season_modal_close"
                            @click="close">
                        <i class="icon-x" aria-hidden="true"></i>
                    </button>

                    <header class="season-modal-head">
                        <span ref="badge" class="season-modal-emblem" aria-hidden="true">
                            <svg v-if="newYear" viewBox="-12 -12 24 24"><path :d="sparklePath"/></svg>
                            <svg v-else viewBox="-12 -12 24 24"><path :d="starPath"/></svg>
                        </span>
                        <p class="season-modal-hello">
                            {{ dayGreeting }}<template v-if="firstName">, {{ firstName }}</template>
                        </p>
                        <h2 id="season-modal-title" class="season-modal-title">
                            {{ title }}
                        </h2>
                        <p class="season-modal-message">{{ calendarVisible ? adventIntro : message }}</p>
                        <div v-if="countdown && !reviewing" class="season-modal-count" :class="{'is-day': countdown.count === null}">
                            <strong v-if="countdown.count !== null">{{ countdown.count }}</strong>
                            <span>{{ countdown.unit }} {{ countdown.label }}</span>
                        </div>
                    </header>

                    <!-- Natal: as 24 portas -->
                    <div v-if="calendarVisible" class="season-calendar" role="list" aria-label="Portas do Advento">
                        <div v-for="door in doors" :key="door.day" class="season-calendar-slot" role="listitem"
                             :style="{'--i': door.day}">
                            <button ref="doors" type="button" class="season-cal-door" :data-day="door.day"
                                    :class="[`tone-${door.tone}`, {'is-open': door.open, 'is-today': door.today,
                                                                   'is-locked': door.locked, 'is-last': door.day === 24}]"
                                    :aria-disabled="door.locked ? 'true' : 'false'" :aria-label="door.label"
                                    :title="door.locked ? door.label : null" @click="openDoor(door.day)">
                                <span class="season-cal-inside" aria-hidden="true">
                                    <i :class="door.icon"></i>
                                    <small>{{ door.title }}</small>
                                </span>
                                <span class="season-cal-leaf" aria-hidden="true">
                                    <span class="season-cal-number">{{ door.day }}</span>
                                    <span v-if="door.today" class="season-cal-today">hoje</span>
                                    <i v-else-if="door.locked" class="icon-lock season-cal-lock"></i>
                                    <span class="season-cal-knob"></span>
                                </span>
                            </button>
                        </div>
                    </div>

                    <!-- Réveillon: brinde com fogos na tela inteira -->
                    <div v-else-if="newYear" class="season-modal-toast-wrap">
                        <button ref="toast" type="button" class="season-modal-toast" data-cy="season_modal_toast"
                                :class="{'is-clinking': clinking}" @click="toast">
                            <svg viewBox="0 0 40 32" aria-hidden="true">
                                <g class="season-glass season-glass-left">
                                    <path d="M9 8.5 L16.2 8.5 L15.5 13 Q12.5 16.5 9.5 13 Z" fill="currentColor"/>
                                    <path d="M8 3 L17 3 L15.5 13 Q12.5 16.5 9.5 13 Z M12.5 15 L12.5 25 M9 26 L16 26" fill="none"
                                          stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                </g>
                                <g class="season-glass season-glass-right">
                                    <path d="M24 8.5 L31.2 8.5 L30.5 13 Q27.5 16.5 24.5 13 Z" fill="currentColor"/>
                                    <path d="M23 3 L32 3 L30.5 13 Q27.5 16.5 24.5 13 Z M27.5 15 L27.5 25 M24 26 L31 26" fill="none"
                                          stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                </g>
                            </svg>
                            Brindar com a equipe
                        </button>
                    </div>

                    <!-- Depois do Natal (até 11/1): as cartinhas do Advento continuam guardadas para reler -->
                    <div v-if="!calendarVisible" class="season-modal-review-wrap">
                        <button type="button" class="season-modal-review" data-cy="season_modal_review" @click="startReview">
                            <span class="season-modal-review-icon" aria-hidden="true"><i class="icon-gift"></i></span>
                            <span class="season-modal-review-text">
                                <strong>Reler as mensagens do Advento</strong>
                                <small>As 24 cartinhas do Natal ficam guardadas aqui até 11 de janeiro</small>
                            </span>
                            <i class="icon-chevron-right season-modal-review-arrow" aria-hidden="true"></i>
                        </button>
                    </div>

                    <footer class="season-modal-foot">
                        <div v-if="calendarVisible && availableDay" class="season-modal-progress">
                            <span>{{ openedCount }} de 24 portas abertas</span>
                            <span class="season-modal-progress-bar" aria-hidden="true">
                                <span :style="{width: `${openedCount / 24 * 100}%`}"></span>
                            </span>
                        </div>
                        <p class="season-modal-sign">Com carinho, equipe R2 Soft</p>
                    </footer>

                    <!-- A surpresa da porta: uma cartinha por cima do calendário -->
                    <transition name="season-letter">
                        <div v-if="activeDoor" class="season-letter-layer" @mousedown.self="activeDay = null">
                            <article ref="letter" class="season-letter" :class="{'is-grand': activeDoor.day === 24}"
                                     :key="activeDoor.day" tabindex="-1"
                                     aria-live="polite" :aria-label="`Surpresa do dia ${activeDoor.day}`">
                                <!-- Papel: recebe o brilho que passa e a luz do dia 24 (recortado nas bordas) -->
                                <span class="season-letter-paper" aria-hidden="true"></span>
                                <span class="season-letter-seal" aria-hidden="true"><i :class="activeDoor.icon"></i></span>
                                <!-- As linhas aparecem uma a uma, como quem lê devagar -->
                                <small class="season-letter-date season-letter-line" style="--d: 0">{{ activeDoor.day }} de dezembro</small>
                                <span class="season-letter-flourish season-letter-line" style="--d: 1" aria-hidden="true">✦</span>
                                <h3 class="season-letter-title season-letter-line" style="--d: 2">{{ activeDoor.title }}</h3>
                                <p class="season-letter-text season-letter-line" style="--d: 3">{{ activeDoor.text }}</p>
                                <span class="season-letter-sign season-letter-line" style="--d: 5">equipe R2 Soft</span>
                                <div class="season-letter-nav season-letter-line" style="--d: 6">
                                    <button type="button" class="season-letter-step" :disabled="!previousDay"
                                            aria-label="Porta anterior" @click="openDoor(previousDay)">
                                        <i class="icon-chevron-left" aria-hidden="true"></i>
                                    </button>
                                    <button type="button" class="season-letter-back" data-cy="season_letter_back"
                                            @click="activeDay = null">
                                        Voltar ao calendário
                                    </button>
                                    <button type="button" class="season-letter-step" :disabled="!nextDay"
                                            aria-label="Próxima porta" @click="openDoor(nextDay)">
                                        <i class="icon-chevron-right" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </article>
                        </div>
                    </transition>
                </section>
            </div>
        </transition>
    </teleport>
</template>

<script>
// Modal de festas, aberto pelo selo do cabeçalho. De novembro ao Natal é o calendário do Advento: 24 portas, cada
// uma com uma frase e uma surpresa diferente ao abrir (seasonDoorEffects.js); em novembro as portas aparecem
// trancadas, como prévia. Depois do Natal vira um cartão com a saudação, a contagem e, no Réveillon, o brinde com
// fogos na tela inteira; até 11/1 o cartão tem "Reler as mensagens do Advento", que volta às 24 portas liberadas.
import {mapMutations, mapState} from 'vuex';
import SeasonSky from '@/components/season/SeasonSky.vue';
import seasonBurst from '@/Helpers/global/seasonBurst';
import playDoorEffect from '@/Helpers/global/seasonDoorEffects';
import {showFireworks} from '@/Helpers/global/seasonFireworks';
import {ADVENT_DOORS} from '@/Helpers/global/seasonAdvent';
import {firstNameOf, SPARKLE_PATH, STAR_PATH} from '@/Helpers/global/seasonShapes';
import {
    adventToday, dayPeriodGreeting, saveAdventPeek, openedAdventDoors, saveAdventDoor, seasonCountdown, seasonGreeting, seasonMessage,
} from '@/Helpers/global/season';

// Cores das portas, alternadas como num calendário de papel: esmeralda, rubi e verde-noite
const TONES = ['emerald', 'ruby', 'pine', 'ruby', 'emerald', 'pine'];

// Estrelinhas do fundo do cartão: posição, tamanho e ritmo fixos (gerados uma vez, sem sortear a cada abertura)
const TWINKLES = Array.from({length: 34}, (_, i) => {
    const x = (i * 37.3) % 100;
    const y = (i * 53.7 + 11) % 100;
    const size = 1.5 + (i % 4) * 0.7;
    return {id: i, style: {left: `${x}%`, top: `${y}%`, width: `${size}px`, height: `${size}px`,
        animationDelay: `${(i * 0.73) % 5}s`, animationDuration: `${3.5 + (i % 5) * 0.9}s`}};
});

// Enfeites pendurados: fio dourado fino com uma estrelinha ou uma bolinha na ponta
const HANGERS = [
    {left: 7, length: 54, kind: 'star', size: 15},
    {left: 15, length: 30, kind: 'ball', size: 9},
    {left: 22, length: 70, kind: 'ruby', size: 11},
    {left: 78, length: 44, kind: 'ruby', size: 10},
    {left: 85, length: 66, kind: 'star', size: 13},
    {left: 92, length: 28, kind: 'ball', size: 8},
].map((hanger, i) => ({
    id: i, kind: hanger.kind,
    style: {left: `${hanger.left}%`, '--length': `${hanger.length}px`, '--size': `${hanger.size}px`,
        animationDelay: `${-i * 0.9}s`, animationDuration: `${5 + (i % 3)}s`},
}));

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export default {
    name: 'SeasonModal',
    components: {SeasonSky},
    data() {
        return {
            starPath: STAR_PATH,
            sparklePath: SPARKLE_PATH,
            twinkles: TWINKLES,
            hangers: HANGERS,
            firstName: '',
            openedDoors: [],
            activeDay: null,
            clinking: false,
            // Depois do Natal: relendo as cartinhas do Advento
            reviewing: false,
        };
    },
    computed: {
        ...mapState({
            open: 'seasonCardOpen',
            now: 'seasonNow',
            phase: 'seasonPhase',
            newYear: 'seasonNewYear',
            effects: 'seasonEffects',
        }),
        greeting() {
            return seasonGreeting(this.now);
        },
        dayGreeting() {
            return dayPeriodGreeting(this.now);
        },
        message() {
            return seasonMessage(this.now);
        },
        countdown() {
            return seasonCountdown(this.now);
        },
        showAdvent() {
            return ['november', 'advent', 'christmasEve', 'christmas'].includes(this.phase);
        },
        // Depois do Natal o modal vira o cartão de fim de ano; até 11/1 dá para voltar ao calendário e reler tudo
        calendarVisible() {
            return this.showAdvent || this.reviewing;
        },
        title() {
            if (this.reviewing) return 'Mensagens do Advento';
            return this.showAdvent ? 'Calendário do Advento' : `${this.greeting}!`;
        },
        // O Advento é sempre do dezembro que passou (em janeiro, do ano anterior)
        adventYear() {
            const year = this.now.getFullYear();
            return this.now.getMonth() === 0 ? year - 1 : year;
        },
        // Última porta liberada: a de hoje no Advento; relendo depois do Natal, todas
        availableDay() {
            return this.reviewing ? 24 : this.adventDay;
        },
        // Porta de hoje (1 a 24; no Natal, todas); 0 em novembro
        adventDay() {
            return adventToday(this.now);
        },
        adventIntro() {
            if (this.reviewing) return 'Reviva cada recado deste Natal. As 24 cartinhas ficam guardadas aqui até 11 de janeiro.';
            if (!this.adventDay) return 'A primeira porta abre em 1º de dezembro. Até lá, as surpresas ficam guardadas.';
            if (this.phase === 'christmas') return 'Feliz Natal! Todas as portas estão abertas para você reviver cada surpresa.';
            const today = this.doors[this.adventDay - 1];
            if (today && !today.open) return 'Uma porta por dia até o Natal. A de hoje está esperando por você.';
            return 'Uma porta por dia até o Natal. Volte amanhã para a próxima surpresa.';
        },
        doors() {
            return ADVENT_DOORS.map((door, index) => {
                const locked = door.day > this.availableDay;
                // Porta trancada fica sempre fechada (mesmo se o relógio do computador voltou no tempo)
                const open = !locked && this.openedDoors.includes(door.day);
                let label = `Abrir a porta do dia ${door.day}`;
                if (locked) label = `A porta do dia ${door.day} abre em ${door.day} de dezembro`;
                else if (open) label = `Ver de novo a surpresa do dia ${door.day}: ${door.title}`;
                return {
                    ...door, locked, open, label,
                    tone: TONES[(index + Math.floor(index / 6)) % TONES.length],
                    // No Natal todas as portas ficam liberadas e nenhuma é "a de hoje"
                    today: !this.reviewing && door.day === this.adventDay && this.phase !== 'christmas',
                };
            });
        },
        openedCount() {
            return this.doors.filter(door => door.open).length;
        },
        activeDoor() {
            return ADVENT_DOORS.find(door => door.day === this.activeDay) || null;
        },
        // Navegação da cartinha: só entre portas já liberadas
        previousDay() {
            return this.activeDay > 1 ? this.activeDay - 1 : null;
        },
        nextDay() {
            return this.activeDay && this.activeDay < this.availableDay ? this.activeDay + 1 : null;
        },
    },
    watch: {
        open(open) {
            if (open) {
                this.opener = document.activeElement;
                this.activeDay = null;
                this.reviewing = false;
                this.openedDoors = openedAdventDoors(this.adventYear);
                this.firstName = this.readFirstName();
                // Novembro: espiou a prévia; o convite "Advento em N dias" sai do selo
                if (this.phase === 'november') saveAdventPeek(this.adventYear);
                document.documentElement.classList.add('season-modal-lock');
            } else {
                document.documentElement.classList.remove('season-modal-lock');
            }
        },
        adventYear(year) {
            this.openedDoors = openedAdventDoors(year);
        },
        // A época acabou ou voltou para o Advento com o modal aberto: sai do modo de releitura
        showAdvent(advent) {
            if (advent) this.reviewing = false;
        },
        activeDay(day) {
            if (day) this.$nextTick(() => this.$refs.letter?.focus({preventScroll: true}));
        },
    },
    beforeUnmount() {
        clearTimeout(this.clinkTimer);
        clearTimeout(this.effectTimer);
        document.documentElement.classList.remove('season-modal-lock');
        this.setSeasonCardOpen(false);
    },
    methods: {
        ...mapMutations(['setSeasonCardOpen']),
        readFirstName() {
            try {
                return firstNameOf(JSON.parse(localStorage.getItem('user'))?.name);
            } catch (e) {
                return '';
            }
        },
        close() {
            this.setSeasonCardOpen(false);
        },
        openDoor(day) {
            if (!day || day > this.availableDay) return;
            const door = (this.$refs.doors || []).find(el => Number(el.dataset.day) === day);
            const firstTime = !this.openedDoors.includes(day);
            this.openedDoors = saveAdventDoor(this.adventYear, day);
            const effect = ADVENT_DOORS.find(item => item.day === day)?.effect;
            clearTimeout(this.effectTimer);
            if (this.activeDay) {
                // Navegando pela cartinha: troca na hora e solta a surpresa da nova porta
                this.activeDay = day;
                this.effectTimer = setTimeout(() => this.playEffect(effect, door), 120);
                return;
            }
            // A porta gira primeiro (mais devagar na primeira vez); depois a cartinha sobe com a surpresa
            this.effectTimer = setTimeout(() => {
                this.activeDay = day;
                // Espera a cartinha assentar para a surpresa sair do selo de cera, no lugar certo
                this.effectTimer = setTimeout(() => this.playEffect(effect, door), 320);
            }, firstTime ? 650 : 280);
        },
        // A surpresa sai do selo da cartinha (ou da porta, se a cartinha ainda não apareceu)
        startReview() {
            this.openedDoors = openedAdventDoors(this.adventYear);
            this.reviewing = true;
            this.$nextTick(() => this.$refs.dialog?.focus({preventScroll: true}));
        },
        stopReview() {
            this.activeDay = null;
            this.reviewing = false;
            this.$nextTick(() => this.$refs.dialog?.querySelector('.season-modal-review')?.focus({preventScroll: true}));
        },
        playEffect(effect, door) {
            this.$nextTick(() => {
                const origin = this.$refs.letter?.querySelector('.season-letter-seal') || door;
                const paper = this.$refs.letter?.querySelector('.season-letter-paper');
                playDoorEffect(effect, {card: this.$refs.dialog, door: origin, badge: this.$refs.badge, paper});
            });
        },
        toast() {
            showFireworks();
            seasonBurst(this.$refs.toast, {newYear: true, origin: {x: 0.5, y: 0.2}, amount: 26});
            this.clinking = false;
            this.$nextTick(() => {
                this.clinking = true;
                clearTimeout(this.clinkTimer);
                this.clinkTimer = setTimeout(() => (this.clinking = false), 900);
            });
        },
        onKeydown(event) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                if (this.activeDay) this.activeDay = null;
                else if (this.reviewing) this.stopReview();
                else this.close();
                return;
            }
            if (event.key === 'Tab') this.trapFocus(event);
        },
        // O foco fica preso dentro do modal (na cartinha, quando ela está aberta)
        trapFocus(event) {
            const scope = this.activeDay ? this.$refs.letter : this.$refs.dialog;
            const items = [...(scope?.querySelectorAll(FOCUSABLE) || [])].filter(el => el.offsetParent !== null);
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            if (event.shiftKey && (document.activeElement === first || !scope.contains(document.activeElement))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !scope.contains(document.activeElement))) {
                event.preventDefault();
                first.focus();
            }
        },
        focusStart() {
            const today = this.$refs.dialog?.querySelector('.season-cal-door.is-today:not(.is-open)');
            (today || this.$refs.dialog)?.focus({preventScroll: true});
        },
        restoreFocus() {
            const target = this.opener?.isConnected ? this.opener : document.querySelector('.season-seal-button');
            target?.focus?.({preventScroll: true});
        },
    },
};
</script>
